import { useRef, useState } from "react";
import { useCest } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { CestDto, SalvarCestRequest } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search, Tag, Plus, Pencil, Trash2,
  Upload, FileText, CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── CSV preview row ────────────────────────────────────────────────────────
interface CsvRow {
  codigoCest: string;
  descricao:  string;
  segmento:   string;
  codigoNcm:  string;
  anexo:      string;
}

// ── Segmentos oficiais (Anexo I do Convênio ICMS 142/18) ───────────────────
// O código do segmento = 2 primeiros dígitos do CEST.
const SEGMENTOS: { codigo: string; nome: string }[] = [
  { codigo: "01", nome: "Autopeças" },
  { codigo: "02", nome: "Bebidas alcoólicas, exceto cerveja e chope" },
  { codigo: "03", nome: "Cervejas, chopes, refrigerantes, águas e outras bebidas" },
  { codigo: "04", nome: "Cigarros e outros produtos derivados do fumo" },
  { codigo: "05", nome: "Cimentos" },
  { codigo: "06", nome: "Combustíveis e lubrificantes" },
  { codigo: "07", nome: "Energia elétrica" },
  { codigo: "08", nome: "Ferramentas" },
  { codigo: "09", nome: 'Lâmpadas, reatores e "starter"' },
  { codigo: "10", nome: "Materiais de construção e congêneres" },
  { codigo: "11", nome: "Materiais de limpeza" },
  { codigo: "12", nome: "Materiais elétricos" },
  { codigo: "13", nome: "Medicamentos de uso humano e outros produtos farmacêuticos para uso humano ou veterinário" },
  { codigo: "14", nome: "Papéis, plásticos, produtos cerâmicos e vidros" },
  { codigo: "16", nome: "Pneumáticos, câmaras de ar e protetores de borracha" },
  { codigo: "17", nome: "Produtos alimentícios" },
  { codigo: "19", nome: "Produtos de papelaria" },
  { codigo: "20", nome: "Produtos de perfumaria e de higiene pessoal e cosméticos" },
  { codigo: "21", nome: "Produtos eletrônicos, eletroeletrônicos e eletrodomésticos" },
  { codigo: "22", nome: "Rações para animais domésticos" },
  { codigo: "23", nome: "Sorvetes e preparados para fabricação de sorvetes em máquinas" },
  { codigo: "24", nome: "Tintas e vernizes" },
  { codigo: "25", nome: "Veículos automotores" },
  { codigo: "26", nome: "Veículos de duas e três rodas motorizados" },
  { codigo: "28", nome: "Venda de mercadorias pelo sistema porta a porta" },
];

/** Deriva o nome do segmento a partir dos 2 primeiros dígitos do código CEST. */
const segmentoDoCest = (codigo: string): string | undefined =>
  SEGMENTOS.find(s => s.codigo === codigo.replace(/\D/g, "").slice(0, 2))?.nome;

// ── Form vazio ─────────────────────────────────────────────────────────────
const EMPTY: SalvarCestRequest = {
  codigo:    "",
  descricao: "",
  segmento:  "",
  codigoNcm: "",
  anexo:     "",
};

function parseCsv(text: string): CsvRow[] {
  const linhas = text.split(/\r?\n/).filter(l => l.trim());
  if (linhas.length <= 1) return [];

  return linhas.slice(1).flatMap(linha => {
    const c = linha.split(';');
    // Tenta detectar formato (7 colunas com índice) ou compacto (6 colunas)
    const comIndice = c.length >= 7;
    const codigoCest = (comIndice ? c[1] : c[0]).trim();
    if (!codigoCest) return [];
    return [{
      codigoCest,
      descricao:  (comIndice ? c[2] : c[1]).trim(),
      segmento:   (comIndice ? c[3] : c[2]).trim(),
      codigoNcm:  (comIndice ? c[5] : c[3]).trim(),
      anexo:      (comIndice ? (c[6] ?? '') : (c[4] ?? '')).trim(),
    }];
  });
}

export default function Cest() {
  const [busca, setBusca]           = useState("");
  const [segmentoFiltro, setSegmentoFiltro] = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarCestRequest>(EMPTY);
  const [saving, setSaving]         = useState(false);

  // Import
  const fileRef                          = useRef<HTMLInputElement>(null);
  const [csvRows, setCsvRows]            = useState<CsvRow[]>([]);
  const [csvFileName, setCsvFileName]    = useState("");
  const [importing, setImporting]        = useState(false);
  const [importResult, setImportResult]  = useState<{ inseridos: number; atualizados: number; erros: number } | null>(null);

  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;

  const { query, obterPorId, criar, atualizar, excluir, importar } = useCest({
    busca:    busca || undefined,
    segmento: segmentoFiltro || undefined,
    pagina,
    tamanhoPagina,
  });

  const itens = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarCestRequest, value: string | null) =>
    setForm(f => ({ ...f, [field]: value }));

  // ── CRUD ──────────────────────────────────────────────────────────────
  const openNew = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    try {
      const dto: CestDto = await obterPorId(id);
      setEditId(id);
      setForm({ codigo: dto.codigo, descricao: dto.descricao, segmento: dto.segmento, codigoNcm: dto.codigoNcm, anexo: dto.anexo });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar CEST.");
    }
  };

  const handleSave = async () => {
    if (!form.codigo?.trim()) { toast.error("Código CEST é obrigatório."); return; }
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("CEST atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("CEST criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar CEST.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("CEST excluído.");
    } catch {
      toast.error("Erro ao excluir CEST.");
    } finally {
      setDeleteId(null);
    }
  };

  // ── Importação CSV ─────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result as string;
      const rows = parseCsv(text);
      setCsvRows(rows);
      if (rows.length === 0) toast.warning("Nenhum dado válido encontrado no arquivo.");
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecione um arquivo primeiro."); return; }
    setImporting(true);
    try {
      const result = await importar.mutateAsync(file);
      setImportResult(result);
      setCsvRows([]);
      setCsvFileName("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success(`Importação concluída: ${result.inseridos} inseridos, ${result.atualizados} atualizados.`);
    } catch {
      toast.error("Erro durante a importação.");
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setCsvRows([]);
    setCsvFileName("");
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="h-6 w-6" /> CEST
          </h1>
          <p className="text-muted-foreground">Código Especificador da Substituição Tributária</p>
        </div>
        {podeEditar && (
          <Button onClick={openNew}>
            <Plus className="h-4 w-4 mr-2" /> Novo CEST
          </Button>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      <Tabs defaultValue="consulta">
        <TabsList>
          <TabsTrigger value="consulta">Consulta</TabsTrigger>
          {podeEditar && <TabsTrigger value="importar">Importar Tabela</TabsTrigger>}
        </TabsList>

        {/* ── Aba Consulta ───────────────────────────────────────────────── */}
        <TabsContent value="consulta" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Código CEST, descrição ou NCM..."
                className="pl-9"
                value={busca}
                onChange={e => { setBusca(e.target.value); setPagina(1); }}
              />
            </div>
            <Select
              value={segmentoFiltro || "__todos__"}
              onValueChange={v => { setSegmentoFiltro(v === "__todos__" ? "" : v); setPagina(1); }}
            >
              <SelectTrigger className="w-72"><SelectValue placeholder="Todos os segmentos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todos__">Todos os segmentos</SelectItem>
                {SEGMENTOS.map(s => (
                  <SelectItem key={s.codigo} value={s.nome}>{s.codigo} — {s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">Código CEST</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="w-[200px]">Segmento</TableHead>
                  <TableHead className="w-[110px]">NCM/SH</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : itens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum CEST encontrado.</TableCell>
                  </TableRow>
                ) : itens.map(item => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono font-semibold">{item.codigo ?? "—"}</TableCell>
                    <TableCell className="text-sm">{item.descricao ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{item.segmento ?? "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{item.codigoNcm ?? "—"}</TableCell>
                    <TableCell>
                      {podeEditar && (
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(item.id)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {query.data && (
            <PaginacaoRodape
              pagina={pagina}
              totalPaginas={query.data.totalPaginas}
              totalItens={totalItens}
              primeiroItem={primeiroItem}
              ultimoItem={ultimoItem}
              tamanhoPagina={tamanhoPagina}
              onPaginaChange={setPagina}
              onTamanhoPaginaChange={setTamanhoPagina}
            />
          )}
        </TabsContent>

        {/* ── Aba Importar ───────────────────────────────────────────────── */}
        {podeEditar && (
        <TabsContent value="importar" className="space-y-4 mt-4">
          <div className="border rounded-lg p-5 bg-muted/30 space-y-4">
            <div>
              <h3 className="font-semibold mb-1">Importação da Tabela CEST</h3>
              <p className="text-sm text-muted-foreground">
                Selecione o arquivo TXT/CSV da tabela CEST. Formato esperado: colunas separadas por <code className="bg-muted px-1 rounded">;</code> com cabeçalho na 1ª linha.
                <br />Colunas: <strong>CEST ; Descrição ; Segmento ; Item ; NCM/SH ; Anexo</strong>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 border rounded-md px-3 py-2 bg-background text-sm text-muted-foreground truncate">
                {csvFileName || "Nenhum arquivo selecionado"}
              </div>
              <Button variant="outline" onClick={() => fileRef.current?.click()} type="button">
                <FileText className="h-4 w-4 mr-2" /> Selecionar arquivo
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".txt,.csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {csvRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{csvRows.length} registro(s) para importar:</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={resetImport}>Limpar</Button>
                    <Button size="sm" onClick={handleImport} disabled={importing}>
                      <Upload className="h-4 w-4 mr-2" />
                      {importing ? "Importando..." : "Confirmar Importação"}
                    </Button>
                  </div>
                </div>

                {/* Prévia */}
                <div className="border rounded-lg overflow-auto max-h-72">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">CEST</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="w-[180px]">Segmento</TableHead>
                        <TableHead className="w-[110px]">NCM/SH</TableHead>
                        <TableHead className="w-[200px]">Anexo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvRows.slice(0, 100).map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{row.codigoCest}</TableCell>
                          <TableCell className="text-xs">{row.descricao}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{row.segmento}</TableCell>
                          <TableCell className="font-mono text-xs">{row.codigoNcm}</TableCell>
                          <TableCell className="text-xs text-muted-foreground truncate max-w-[200px]">{row.anexo}</TableCell>
                        </TableRow>
                      ))}
                      {csvRows.length > 100 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-2">
                            ...e mais {csvRows.length - 100} registros
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Resultado da importação */}
            {importResult && (
              <div className="flex items-start gap-3 p-4 bg-background border rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">Importação concluída com sucesso</p>
                  <div className="flex gap-3 text-sm">
                    <Badge variant="default">{importResult.inseridos} inseridos</Badge>
                    <Badge variant="secondary">{importResult.atualizados} atualizados</Badge>
                    {importResult.erros > 0 && (
                      <Badge variant="destructive">{importResult.erros} erros</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
        )}
      </Tabs>

      {/* Dialog Criar / Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar CEST" : "Novo CEST"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Código CEST <span className="text-destructive">*</span></Label>
                <Input
                  value={form.codigo ?? ""}
                  onChange={e => {
                    const codigo = e.target.value;
                    const seg = segmentoDoCest(codigo);
                    setForm(f => ({ ...f, codigo, segmento: seg ?? f.segmento }));
                  }}
                  maxLength={9}
                  placeholder="Ex.: 01.001.00"
                />
              </div>
              <div className="space-y-1">
                <Label>NCM/SH</Label>
                <Input
                  value={form.codigoNcm ?? ""}
                  onChange={e => set("codigoNcm", e.target.value)}
                  maxLength={15}
                  placeholder="Ex.: 8704.21.10"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao ?? ""}
                onChange={e => set("descricao", e.target.value)}
                maxLength={700}
                rows={3}
              />
            </div>

            <div className="space-y-1">
              <Label>Segmento</Label>
              <Select value={form.segmento || ""} onValueChange={v => set("segmento", v)}>
                <SelectTrigger><SelectValue placeholder="Selecione o segmento (ou preenche pelo código)..." /></SelectTrigger>
                <SelectContent>
                  {SEGMENTOS.map(s => (
                    <SelectItem key={s.codigo} value={s.nome}>{s.codigo} — {s.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Anexo</Label>
              <Textarea
                value={form.anexo ?? ""}
                onChange={e => set("anexo", e.target.value)}
                maxLength={700}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir CEST?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
